import {
  createWebTelemetry,
  getWebTelemetry,
  type TelemetryClient,
} from "@lala/telemetry-sdk/web";

import { telemetrySettings } from "./config.ts";

/**
 * El cliente de telemetría (`@lala/telemetry-sdk/web`), o `null` si no hay.
 *
 * Singleton: la primera llamada lo crea --init y autocaptura de clicks,
 * scroll, web vitals, errores y formularios-- y las siguientes devuelven el
 * mismo. Sin configuración (`config.ts`) devuelve `null` y no se crea nada, así
 * que tampoco sale ninguna petición.
 *
 * La autocaptura va tal cual la manda el SDK, con el texto visible de cada
 * click y el mensaje de cada error: se decidió enviar datos personales y
 * texto para poder analizar el uso completo (#5).
 */
let override: TelemetryClient | null | undefined;

export function getTelemetry(): TelemetryClient | null {
  if (override !== undefined) return override;
  if (telemetrySettings === null || typeof window === "undefined") return null;
  try {
    const existing = getWebTelemetry();
    if (existing !== null) return existing;
    return createWebTelemetry({ ...telemetrySettings });
  } catch {
    // El SDK nunca rompe la aplicación: sin cliente se sigue sin telemetría.
    return null;
  }
}

/**
 * Sustituye el cliente en las pruebas. `undefined` vuelve al de verdad.
 *
 * Las pruebas no tienen configuración (`config.ts`), así que sin esto no hay
 * cliente y no se puede mirar qué eventos salen.
 */
export function setTelemetryForTesting(
  client: TelemetryClient | null | undefined,
): void {
  override = client;
}

/**
 * Deja la cola vacía ya y, con `unload`, por `sendBeacon`: es lo que sobrevive
 * a que el navegador se vaya (el logout navega al SSO).
 */
export function flushTelemetry(unload: boolean): void {
  try {
    getTelemetry()?.flush(unload);
  } catch {
    // Nada que hacer: se pierde lo que quedara en la cola.
  }
}
